/**
 * CampaignStatesPage — campaign lifecycle inspector.
 *
 * Flow:
 *   1. User enters a campaign id.
 *   2. Page loads /history + /allowed for that campaign.
 *   3. Shows a current-state pill, transition buttons gated by the allowed
 *      next-states list, and an append-only audit log.
 *
 * Brand: emerald -> sky gradient headlines, light theme, 6-8px rounded
 * corners, zinc-200 borders, subtle shadow. No orange, no purple, no
 * dark mode, no emojis.
 */
import { useMemo, useState, type FormEvent } from "react";
import type { CampaignState, CampaignStateLogRow } from "./api";
import {
	useCampaignStatesAllowed,
	useCampaignStatesHistory,
	useCampaignStatesTransition,
} from "./hooks";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-sky-600 bg-sky-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-800 transition-colors hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATE_LABELS: Record<CampaignState, string> = {
	draft: "Draft",
	inactive: "Inactive",
	live: "Live",
	paused: "Paused",
	archived: "Archived",
};

function statePillClasses(state: CampaignState | null | undefined): string {
	const base =
		"inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold";
	if (!state) return `${base} border-zinc-200 bg-zinc-50 text-zinc-500`;
	switch (state) {
		case "live":
			return `${base} border-emerald-200 bg-emerald-50 text-emerald-700`;
		case "paused":
			return `${base} border-sky-200 bg-sky-50 text-sky-700`;
		case "draft":
			return `${base} border-zinc-200 bg-zinc-50 text-zinc-700`;
		case "inactive":
			return `${base} border-zinc-200 bg-zinc-100 text-zinc-700`;
		case "archived":
			return `${base} border-zinc-300 bg-zinc-200 text-zinc-700`;
	}
}

function formatTimestamp(ms: number): string {
	try {
		const d = new Date(ms);
		const pad = (n: number) => String(n).padStart(2, "0");
		return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(
			d.getUTCDate(),
		)} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
	} catch {
		return String(ms);
	}
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function CampaignStatesPage() {
	const [draftId, setDraftId] = useState("");
	const [campaignId, setCampaignId] = useState("");
	const [reason, setReason] = useState("");

	const historyQuery = useCampaignStatesHistory(campaignId);
	const allowedQuery = useCampaignStatesAllowed(campaignId);
	const transitionMut = useCampaignStatesTransition();

	const history: CampaignStateLogRow[] = useMemo(
		() => historyQuery.data?.history ?? [],
		[historyQuery.data],
	);
	const currentState: CampaignState | null = historyQuery.data?.current_state
		? historyQuery.data.current_state
		: allowedQuery.data?.current_state ?? null;
	const allowedNext: CampaignState[] = allowedQuery.data?.allowed_next ?? [];

	const handleLoad = (e: FormEvent) => {
		e.preventDefault();
		const id = draftId.trim();
		if (!id) return;
		setCampaignId(id);
	};

	const handleTransition = (to: CampaignState) => {
		if (!campaignId) return;
		transitionMut.mutate(
			{
				campaign_id: campaignId,
				to_state: to,
				reason: reason.trim() || undefined,
			},
			{
				onSuccess: () => setReason(""),
			},
		);
	};

	const isBusy = transitionMut.isPending;

	return (
		<main
			data-testid="campaign-states-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					campaign-states
				</p>
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
					Campaign lifecycle
				</h1>
				<p className="text-sm text-zinc-600">
					Inspect a campaign's current state, move it through the lifecycle,
					and review the full audit log of prior transitions.
				</p>
			</header>

			<section className={CARD} aria-label="Load campaign">
				<h2 className="text-base font-semibold text-zinc-900">
					Select a campaign
				</h2>
				<form
					onSubmit={handleLoad}
					className="mt-3 flex flex-col gap-3 sm:flex-row"
					data-testid="campaign-states-load-form"
				>
					<input
						type="text"
						className={INPUT}
						placeholder="Campaign ID"
						value={draftId}
						onChange={(e) => setDraftId(e.currentTarget.value)}
						data-testid="campaign-states-id-input"
						aria-label="Campaign ID"
					/>
					<button
						type="submit"
						className={BTN_PRIMARY}
						disabled={!draftId.trim()}
						data-testid="campaign-states-load-submit"
					>
						Load
					</button>
				</form>
			</section>

			{!campaignId ? (
				<section className={CARD}>
					<p
						className="text-sm text-zinc-500"
						data-testid="campaign-states-empty"
					>
						Enter a campaign ID above to inspect its lifecycle.
					</p>
				</section>
			) : historyQuery.isLoading || allowedQuery.isLoading ? (
				<section className={CARD}>
					<p
						className="text-sm text-zinc-500"
						data-testid="campaign-states-loading"
					>
						Loading campaign state.
					</p>
				</section>
			) : historyQuery.error ? (
				<section className={CARD}>
					<p
						className="text-sm text-red-600"
						role="alert"
						data-testid="campaign-states-error"
					>
						{historyQuery.error.message}
					</p>
				</section>
			) : (
				<>
					<section
						className={CARD}
						aria-label="Current state and transitions"
						data-testid="campaign-states-current"
					>
						<div className="flex flex-wrap items-center justify-between gap-4">
							<div className="flex flex-col gap-1">
								<p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
									Current state
								</p>
								<div className="flex items-center gap-3">
									<span
										className={statePillClasses(currentState)}
										data-testid="campaign-states-current-pill"
									>
										{currentState ? STATE_LABELS[currentState] : "Unknown"}
									</span>
									<span className="font-mono text-xs text-zinc-500">
										{campaignId}
									</span>
								</div>
							</div>
							<button
								type="button"
								className={BTN_SECONDARY}
								onClick={() => {
									setCampaignId("");
									setDraftId("");
								}}
								data-testid="campaign-states-clear"
							>
								Clear
							</button>
						</div>

						<div className="mt-5 flex flex-col gap-3">
							<label className="text-xs font-medium text-zinc-600">
								Reason (optional)
								<input
									type="text"
									className={`${INPUT} mt-1`}
									placeholder="e.g. launched after QA sign-off"
									value={reason}
									onChange={(e) => setReason(e.currentTarget.value)}
									disabled={isBusy}
									data-testid="campaign-states-reason-input"
								/>
							</label>

							<div className="flex flex-wrap gap-2">
								{allowedNext.length === 0 ? (
									<p
										className="text-sm text-zinc-500"
										data-testid="campaign-states-no-transitions"
									>
										No further transitions available.
									</p>
								) : (
									allowedNext.map((next) => (
										<button
											key={next}
											type="button"
											className={BTN_PRIMARY}
											disabled={isBusy}
											onClick={() => handleTransition(next)}
											data-testid={`campaign-states-transition-${next}`}
										>
											Move to {STATE_LABELS[next]}
										</button>
									))
								)}
							</div>

							{transitionMut.error ? (
								<p
									className="text-xs text-red-600"
									role="alert"
									data-testid="campaign-states-transition-error"
								>
									{transitionMut.error.message}
								</p>
							) : null}
						</div>
					</section>

					<section
						className={CARD}
						aria-label="Campaign state history"
						data-testid="campaign-states-history"
					>
						<h2 className="text-base font-semibold text-zinc-900">
							Transition history
						</h2>
						{history.length === 0 ? (
							<p
								className="mt-3 text-sm text-zinc-500"
								data-testid="campaign-states-history-empty"
							>
								No transitions recorded yet.
							</p>
						) : (
							<ul
								className="mt-3 divide-y divide-zinc-200"
								data-testid="campaign-states-history-list"
							>
								{history.map((row) => (
									<li
										key={row.id}
										className="flex flex-col gap-1 py-3 text-sm sm:flex-row sm:items-center sm:justify-between"
										data-testid={`campaign-states-history-row-${row.id}`}
									>
										<div className="flex flex-wrap items-center gap-2">
											<span className={statePillClasses(row.from_state)}>
												{row.from_state
													? STATE_LABELS[row.from_state]
													: "Initial"}
											</span>
											<span className="text-zinc-400">to</span>
											<span className={statePillClasses(row.to_state)}>
												{STATE_LABELS[row.to_state]}
											</span>
											{row.reason ? (
												<span className="text-xs text-zinc-600">
													— {row.reason}
												</span>
											) : null}
										</div>
										<span className="font-mono text-xs text-zinc-500">
											{formatTimestamp(row.transitioned_at)}
										</span>
									</li>
								))}
							</ul>
						)}
					</section>
				</>
			)}
		</main>
	);
}

export default CampaignStatesPage;
